import { formatMoney } from "@majitap/ui";
import { useCompanyFormat } from "../../company";
import type { DeliveryRecord } from "../../deliveries";
import { RouterLink } from "../../router-link";
import page from "../orders/record.module.css";
import styles from "./delivery.module.css";

type Line = DeliveryRecord["lines"][number];

/** Which price a line came from, as staff name it. */
const SCOPE: Record<NonNullable<Line["scope"]>, string> = {
  location: "This place",
  customer: "This customer",
  business: "This business",
  fillsite: "This fill site",
  partner: "This partner",
  zone: "Zone price",
  item: "Item price",
  category: "Category price",
  general: "Any zone",
};

/** Where a line's amount comes from: its rule's scope (the zone by name), its payee, or a voucher. */
function sourceOf(l: Line, d: DeliveryRecord) {
  if (l.direction === "payable" && l.payee_partner) return l.payee_partner.name;
  if (l.scope === "zone" && d.geofence) return d.geofence.name;
  if (l.scope) return SCOPE[l.scope];
  return l.charge_type.code === "discount" ? "Voucher" : null;
}

function Lines({
  title,
  lines,
  d,
  total,
}: {
  title: string;
  lines: Line[];
  d: DeliveryRecord;
  total: [string, string];
}) {
  const format = useCompanyFormat();
  return (
    <div className={page.panel}>
      <div className={styles.moneyTitle}>{title}</div>
      {lines.length === 0 ? <p className={`${page.muted} ${styles.line}`}>None</p> : null}
      {lines.map((l, i) => {
        const source = sourceOf(l, d);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: a delivery's lines have no id and keep their order
          <div key={i} className={styles.line}>
            <span>{l.description ?? l.charge_type.name}</span>
            {source ? <span className={styles.scope}>{source}</span> : <span />}
            <span className={page.amount}>{formatMoney(l.amount, format)}</span>
          </div>
        );
      })}
      <div className={`${styles.line} ${styles.lineTotal}`}>
        <span>{total[0]}</span>
        <span />
        <span className={page.amount}>{formatMoney(total[1], format)}</span>
      </div>
    </div>
  );
}

/**
 * Charges and costs (`Console / Delivery`): what the customer pays and what MajiTap pays for this
 * one delivery, each line with the price it came from, and the margin. Totals are the API's.
 */
export function ChargesSection({ delivery: d }: { delivery: DeliveryRecord }) {
  const format = useCompanyFormat();
  const charges = d.lines.filter((l) => l.direction === "receivable");
  const costs = d.lines.filter((l) => l.direction === "payable");
  return (
    <section className={page.section} aria-label="Charges and costs">
      <div className={page.sectionHead}>
        <div>
          <h2 className={page.sectionTitle}>Charges and costs</h2>
          <p className={page.small}>
            {d.status === "delivered"
              ? "Frozen when it was delivered"
              : "Priced for this place; frozen once it's delivered"}
          </p>
        </div>
        <RouterLink href="/pricing" className={page.link}>
          Rules in Pricing
        </RouterLink>
      </div>
      <div className={styles.money}>
        <Lines title="CUSTOMER PAYS" lines={charges} d={d} total={["Total", d.receivable_total]} />
        <div className={page.stackGap}>
          <Lines title="MAJITAP PAYS" lines={costs} d={d} total={["Costs", d.payable_total]} />
          <div className={page.panel}>
            <div className={page.costRow}>
              <strong>Margin</strong>
              <span className={`${page.amount} ${page.marginValue}`}>{formatMoney(d.margin, format)}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
